import { Injectable } from '@angular/core';

import { VehicleType } from '@dav-core/vehicle-type.enum';
import { VSCCoreProductCodes } from '@dav-model/product-codes';
import { ClaimLimit } from '@dav-model/product-filters/claim-limit';
import { ProductFilter } from '@dav-model/product-filters/product-filter';
import { ProductFilters } from '@dav-model/product-filters/product-filters';
import { ProductItem } from '@dav-model/response/getproducts.response';
import { State } from '@dav-store/reducers';
import { VehicleValues } from '@dav-store/vehicle/vehicle.reducer';
import { selectProductFormValues } from '@dav-store/vehicle/vehicle.selectors';
import { Store } from '@ngrx/store';

let allClaimLimits: ClaimLimit[] = [new ClaimLimit('5K'), new ClaimLimit('15K'), new ClaimLimit('30K')];

@Injectable({
  providedIn: 'root',
})
export class FilterService {
  multiplier = 2;
  private _vehicleValues: VehicleValues | undefined;

  constructor(private store$: Store<State>) {
    this.store$.select(selectProductFormValues).subscribe((vehicleValues: VehicleValues) => {
      this._vehicleValues = vehicleValues;
    }); // i know - the subscription which lives forever...
  }

  fetchFilters(products: ProductItem[]): ProductFilters {
    allClaimLimits = [new ClaimLimit('5K'), new ClaimLimit('15K'), new ClaimLimit('30K')];

    return {
      product: this._fetchFiltersBy('product', products),
      claimLimit: this._fetchFiltersBy('claimLimit', products),
      plan: this._fetchFiltersBy('plan', products),
      term: this._fetchFiltersBy('term', products),
      deductible: this._fetchFiltersBy('deductible', products),
      mileage: this._fetchFiltersBy('mileage', products),
    };
  }

  filter(filters: ProductFilters, products: ProductItem[]): ProductItem[] {
    return this._filterBy(
      'mileage',
      filters,
      this._filterBy(
        'term',
        filters,
        this._filterBy(
          'deductible',
          filters,
          this._filterBy('claimLimit', filters, this._filterBy('plan', filters, products))
        )
      )
    );
  }

  private _matchesBy(property: keyof ProductFilters, filter: ProductFilter, product: ProductItem): boolean {
    switch (property) {
      case 'claimLimit':
        return filter.key === this._productClaimLimit(product).key;
      case 'deductible':
        return filter.key === this._deductibleKey(product);
      case 'mileage':
        return filter.key === product.termMileage.toString();
      case 'plan':
        return filter.key === product.productCode.toString();
      case 'product':
        return filter.key === product.productCode.toString();
      case 'term':
        return filter.key === product.maxTerm.toString();
    }
  }

  private _createFilterBy(property: keyof ProductFilters, product: ProductItem): ProductFilter {
    switch (property) {
      case 'claimLimit':
        return this._productClaimLimit(product);
      case 'deductible':
        return {
          label: this._deductibleKey(product),
          key: this._deductibleKey(product),
          display: true,
          selected: false,
          property: 'deductible',
        } as ProductFilter;
      case 'mileage':
        return {
          label: product.termMileage.toString(),
          key: product.termMileage.toString(),
          display: true,
          selected: false,
          property: 'mileage',
        } as ProductFilter;
      case 'plan':
        return this._getPlan(product);
      case 'product':
        return {
          label: this._productDescription(product),
          key: product.productCode.toString(),
          selected: false,
          display: true,
          property: 'product',
        } as ProductFilter;
      case 'term':
        return {
          label: product.maxTerm.toString(),
          key: product.maxTerm.toString(),
          display: true,
          selected: false,
          property: 'term',
        } as ProductFilter;
    }
  }

  private _fetchFiltersBy(property: keyof ProductFilters, products: ProductItem[]): ProductFilter[] {
    if (products) {
      const filters = products.reduce((prevVal, product) => {
        const index = prevVal.some((filter) => this._matchesBy(property, filter, product));
        if (!index) {
          prevVal.push(this._createFilterBy(property, product));
        }

        return prevVal;
      }, []);

      return this._sortFilters(filters);
    } else {
      return [];
    }
  }

  private _sortFilters(filters: ProductFilter[]) {
    if (filters.length === 0) {
      return filters;
    }
    switch (filters[0].property) {
      case 'claimLimit':
        return allClaimLimits.filter((limit) => filters.find((filter) => filter.key === limit.key));
      case 'deductible':
      case 'term':
      case 'mileage':
        return filters.sort((a, b) => Number(a.label) - Number(b.label));
      case 'plan':
      case 'product':
        return filters.sort((a, b) => a.label.localeCompare(b.label));
    }
  }

  private _getPlan(product: ProductItem) {
    if (product.productCategory === 'MULTICOVERAGE') {
      if (product.isBundled && product.isDiscounted) {
        return {
          label: 'BUNDLES',
          key: product.productCode,
          selected: false,
          display: true,
          property: 'plan',
        } as ProductFilter;
      } else if (product.isBundled && !product.isDiscounted) {
        return {
          label: 'PRODUCT COMBINATIONS',
          key: product.productCode,
          selected: false,
          display: true,
          property: 'plan',
        } as ProductFilter;
      } else {
        return {
          label: this._productPlanName(product),
          key: product.productCode,
          selected: false,
          display: true,
          property: 'plan',
        } as ProductFilter;
      }
    } else {
      return {
        label: this._productPlanName(product),
        key: product.productCode,
        selected: false,
        display: true,
        property: 'plan',
      } as ProductFilter;
    }
  }

  private _filterBy(property: keyof ProductFilters, filters: ProductFilters, products: ProductItem[]) {
    filters[property] = filters[property].map((filter) => ({
      ...filter,
      display: !!products.find((product) => this._matchesBy(property, filter, product)),
    }));

    const selected = filters[property].filter((item) => item.selected && item.display);

    return selected.length
      ? products.filter((product) => selected.find((filter) => this._matchesBy(property, filter, product)))
      : products;
  }

  private _isCoreProduct(product: ProductItem): boolean {
    const pdWords = this._productDescription(product)
      .split(/[\s-_]/)
      .map((word) => word.toUpperCase());

    return (pdWords.includes('CORE') || pdWords.includes('PRINCIPALE')) && !pdWords.includes('NON');
  }

  private _productDescription(product: ProductItem): string {
    return (
      (product.productDescription.enUsProductDescription || '') +
      (product.productDescription.frCaProductDescription || '')
    );
  }

  private _productPlanName(product: ProductItem): string {
    return (product.planName.enUsPlanName || '') + (product.planName.frCaPlanName || '');
  }

  private _productClaimLimit(product: ProductItem): ClaimLimit {
    return allClaimLimits.find((limit) =>
      this._isCoreProduct(product)
        ? this._productPlanName(product)
          .split(' ')
          .some((word) => limit.masks.includes(word))
        : limit.key === '5K'
    );
  }

  private _deductibleKey(product: ProductItem): string {
    let key = product.deductible.toString();
    if (product.reducingDeductible) {
      key += '(R)';
    } else {
      if (this._vehicleValues) {
        const { vehicleType, vehicleCondition } = this._vehicleValues;
        if (
          VSCCoreProductCodes.includes(product.productCode) &&
          vehicleCondition === 'NEW' &&
          (vehicleType.value === VehicleType.Boat || vehicleType.value === VehicleType.Outboard)
        ) {
          key += '(dsl)';
        }
      }
    }

    return key;
  }
}
